import {useState, useCallback, useMemo} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {STALE_TIME_30_SEC} from '@common/constants/time';
import {createCsv} from '@common-web/lib/createCsv';
import {ArrowLeft, FileSpreadsheet, AlertCircle} from 'lucide-react';
import {PageHeader} from '@common-web/components/page-header';
import {StepIndicator} from '@common-web/components/ui/step-indicator';
import {Button} from '@common-web/components/ui/button';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc, createMutationErrorHandler, formatTRPCError} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {reportError} from '@common-web/lib/errorReporting';
import {fileToBase64} from '@common-web/lib/fileToBase64';
import {useProductSelection} from '@client/hooks/useProductSelection';
import {
  STEPS,
  CHAMP_STEPS,
  REQUIRED_MAPPINGS,
  INITIAL_PARSE_STATE,
} from './eligibility-import/types';
import type {ParseState, ColumnMapping, ImportResult} from './eligibility-import/types';
import {
  resolveSystemField,
  parseCSVFile,
  validateRows,
  findDuplicates,
} from './eligibility-import/utils';
import {UploadStep} from './eligibility-import/UploadStep';
import {ValidationStep} from './eligibility-import/ValidationStep';
import {DuplicateCheckStep} from './eligibility-import/DuplicateCheckStep';
import {MappingStep} from './eligibility-import/MappingStep';
import {CoverageProductsStep} from './eligibility-import/CoverageProductsStep';
import {ProcessingStep} from './eligibility-import/ProcessingStep';
import {ResultsStep} from './eligibility-import/ResultsStep';

export const EligibilityImport = () => {
  const {groupId: urlGroupId} = useParams<{groupId: string}>();
  const navigate = useNavigate();

  // Group users with access to exactly one group shouldn't have to pick it —
  // seed the group selector with their only group (they can still change it).
  // vbaGroupId is null until the group is linked to VBA; no prefill in that case.
  const singleGroupProfile = useAuthStore((s) =>
    s.isGroupUser() && s.user?.groupProfiles?.length === 1 && s.user.groupProfiles[0].vbaGroupId
      ? s.user.groupProfiles[0]
      : null,
  );
  const [selectedGroupId, setSelectedGroupId] = useState(singleGroupProfile?.vbaGroupId ?? '');
  const [selectedGroupName, setSelectedGroupName] = useState(singleGroupProfile?.groupName ?? '');
  const [groupSearch, setGroupSearch] = useState('');
  const groupId = urlGroupId || selectedGroupId;

  const groupsQuery = trpc.group.vbaMembers.searchGroups.useQuery(
    {search: groupSearch},
    {enabled: !urlGroupId, staleTime: STALE_TIME_30_SEC, placeholderData: (prev) => prev},
  );

  // Champ groups still assign products at import time; every other tenant
  // (Synrgy, Quantum, etc.) gets products through the analyzer flow after the
  // members are written to VBA. Detect Champ from the VBA group-ID prefix (CH),
  // mirroring the backend's resolveTenantConfigFromGroupId.
  const isChampGroup = groupId.toUpperCase().startsWith('CH');

  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState('');
  const [parseState, setParseState] = useState<ParseState>(INITIAL_PARSE_STATE);
  const [coverageEffectiveDate, setCoverageEffectiveDate] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [jobRunId, setJobRunId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const {
    additionalProductIds,
    allSelectedProductIds,
    getProductName,
    initialProductId,
    productCatalog,
    productsQuery,
    remainingProducts,
    setAdditionalProductIds,
    setInitialProductId,
    toggleAdditionalProduct,
  } = useProductSelection({groupId: isChampGroup ? groupId : undefined});

  const handleGroupChange = (nextGroupId: string, nextGroupName: string) => {
    setSelectedGroupId(nextGroupId);
    setSelectedGroupName(nextGroupName);
    // Products are group-scoped; clear any prior selection so a stale product
    // from a previously chosen group can't be submitted.
    setInitialProductId('');
    setAdditionalProductIds([]);
  };

  const importMutation = trpc.group.vbaMembers.importEligibility.useMutation({
    onSuccess: (data) => {
      setErrorMessage('');
      setJobRunId(data.jobRunId);
      setCurrentStep(5);
    },
    onError: (error) => {
      setErrorMessage(formatTRPCError(error));
      createMutationErrorHandler('Failed to import members')(error);
    },
  });

  const handleFile = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setErrorMessage('');

    const name = selectedFile.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      setErrorMessage(
        'Excel files (.xlsx/.xls) are not supported. Please save your spreadsheet as CSV first (File → Save As → CSV).',
      );
      return;
    }

    let parsed;
    try {
      parsed = await parseCSVFile(selectedFile);
    } catch (error) {
      const message = formatTRPCError(error);
      setErrorMessage(message);
      reportError(error, {
        title: 'Could not parse file',
        description: message,
        context: 'EligibilityImport.handleFile',
      });
      return;
    }

    if (parsed.headers.length === 0) {
      setErrorMessage('Could not parse the file. Please ensure it is a valid CSV.');
      return;
    }

    const initialMappings: ColumnMapping[] = parsed.headers.map((header) => ({
      csvHeader: header,
      systemField: resolveSystemField(header),
    }));

    const {errors, warnings} = validateRows(parsed.headers, parsed.rows);
    const dupes = findDuplicates(parsed.rows, parsed.headers);

    setParseState({
      csvHeaders: parsed.headers,
      csvRows: parsed.rows,
      duplicates: dupes,
      mappings: initialMappings,
      rawRowCount: parsed.rawRowCount,
      totalRows: parsed.totalRows,
      validationErrors: errors,
      validationWarnings: warnings,
    });

    const base64 = await fileToBase64(selectedFile);
    setFileBase64(base64);
    setCurrentStep(1);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (!droppedFile) return;
      const name = droppedFile.name.toLowerCase();
      if (
        name.endsWith('.csv') ||
        name.endsWith('.xlsx') ||
        name.endsWith('.xls') ||
        droppedFile.type === 'text/csv'
      ) {
        handleFile(droppedFile);
      } else {
        setErrorMessage('Please upload a CSV file.');
      }
    },
    [handleFile],
  );

  const updateMapping = (index: number, systemField: string) => {
    setParseState((prev) => ({
      ...prev,
      mappings: prev.mappings.map((m, i) => (i === index ? {...m, systemField} : m)),
    }));
  };

  const canProceedToProducts = () =>
    REQUIRED_MAPPINGS.every((req) => parseState.mappings.some((m) => m.systemField === req));

  const canProceedToImport = () => !!(coverageEffectiveDate && (!isChampGroup || initialProductId));

  const sampleRows = useMemo(() => parseState.csvRows.slice(0, 3), [parseState.csvRows]);

  const handleImport = () => {
    if (!groupId || !coverageEffectiveDate) return;
    if (isChampGroup && !initialProductId) return;
    setErrorMessage('');

    const columnMapping = parseState.mappings
      .filter((m) => m.systemField !== '')
      .map((m) => ({csvHeader: m.csvHeader, systemField: m.systemField}));

    importMutation.mutate({
      columnMapping,
      coverageEffectiveDate,
      fileContent: fileBase64,
      fileName: file?.name || 'import.csv',
      groupId,
      products: isChampGroup ? allSelectedProductIds : [],
    });
  };

  const handleDownloadErrors = () => {
    if (!importResult?.errors.length) return;
    const csv = createCsv(
      importResult.errors.map((err) => ({Row: String(err.row), 'Error Message': err.message})),
    );
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNavigateToMembers = () =>
    urlGroupId ? navigate(`/groups/${urlGroupId}/members`) : navigate('/member-search');

  const reset = () => {
    setCurrentStep(0);
    setFile(null);
    setFileBase64('');
    setParseState(INITIAL_PARSE_STATE);
    setCoverageEffectiveDate('');
    setInitialProductId('');
    setAdditionalProductIds([]);
    setImportResult(null);
    setJobRunId(null);
    setErrorMessage('');
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Bulk Member Import'
          description='Import multiple members from a CSV file'
          icon={<FileSpreadsheet className='h-6 w-6' />}
        >
          <Button
            className='bg-white hover:bg-gray-50 text-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
            onClick={() =>
              urlGroupId ? navigate(`/groups/${urlGroupId}/members`) : navigate('/member-search')
            }
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            {urlGroupId ? 'Back to Members' : 'Back to Search'}
          </Button>
        </PageHeader>

        <StepIndicator steps={isChampGroup ? CHAMP_STEPS : STEPS} currentStep={currentStep} />

        {/* Content */}
        <div className='px-8 py-6 max-w-5xl mx-auto w-full'>
          {errorMessage && (
            <Alert variant='destructive' className='mb-6 border-red-200 bg-red-50'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {currentStep === 0 && (
            <UploadStep
              urlGroupId={urlGroupId}
              selectedGroupId={selectedGroupId}
              selectedGroupName={selectedGroupName}
              onGroupChange={handleGroupChange}
              groupOptions={groupsQuery.data?.data ?? []}
              onGroupSearch={setGroupSearch}
              isLoadingGroups={groupsQuery.isFetching}
              isDragging={isDragging}
              onDragStateChange={setIsDragging}
              onDrop={handleDrop}
              onFileSelect={handleFile}
              file={file}
              totalRows={parseState.totalRows}
              rawRowCount={parseState.rawRowCount}
            />
          )}

          {currentStep === 1 && (
            <ValidationStep
              totalRows={parseState.totalRows}
              rawRowCount={parseState.rawRowCount}
              validationErrors={parseState.validationErrors}
              validationWarnings={parseState.validationWarnings}
              csvHeaders={parseState.csvHeaders}
              onBack={() => setCurrentStep(0)}
              onContinue={() => setCurrentStep(2)}
            />
          )}

          {currentStep === 2 && (
            <DuplicateCheckStep
              duplicates={parseState.duplicates}
              onBack={() => setCurrentStep(1)}
              onContinue={() => setCurrentStep(3)}
            />
          )}

          {currentStep === 3 && (
            <MappingStep
              fileName={file?.name}
              totalRows={parseState.totalRows}
              csvHeaders={parseState.csvHeaders}
              mappings={parseState.mappings}
              sampleRows={sampleRows}
              onUpdateMapping={updateMapping}
              canProceed={canProceedToProducts()}
              onBack={() => setCurrentStep(2)}
              onContinue={() => setCurrentStep(4)}
            />
          )}

          {currentStep === 4 && (
            <CoverageProductsStep
              urlGroupId={urlGroupId}
              groupId={groupId}
              coverageEffectiveDate={coverageEffectiveDate}
              onCoverageEffectiveDateChange={setCoverageEffectiveDate}
              showProductSelection={isChampGroup}
              initialProductId={initialProductId}
              onInitialProductChange={setInitialProductId}
              additionalProductIds={additionalProductIds}
              onToggleAdditionalProduct={toggleAdditionalProduct}
              productCatalog={productCatalog}
              remainingProducts={remainingProducts}
              allSelectedProductIds={allSelectedProductIds}
              getProductName={getProductName}
              isLoadingProducts={productsQuery.isLoading}
              fileName={file?.name}
              totalRows={parseState.totalRows}
              canProceed={canProceedToImport()}
              onImport={handleImport}
              isImporting={importMutation.isPending}
              onBack={() => setCurrentStep(3)}
            />
          )}

          {currentStep === 5 && jobRunId && (
            <ProcessingStep
              jobRunId={jobRunId}
              totalRows={parseState.totalRows}
              fileName={file?.name}
              onComplete={(result: ImportResult) => {
                setImportResult(result);
                setCurrentStep(6);
              }}
              onError={(msg: string) => setErrorMessage(msg)}
            />
          )}

          {currentStep === 6 && importResult && (
            <ResultsStep
              importResult={importResult}
              groupId={groupId}
              onDownloadErrors={handleDownloadErrors}
              onReset={reset}
              urlGroupId={urlGroupId}
              onNavigateToMembers={handleNavigateToMembers}
            />
          )}
        </div>
      </div>
    </div>
  );
};
